import React from "react";
import { graphql, PageProps } from "gatsby";
import RegionTemplate from "@/templates/region/RegionTemplate";
import { RegionData } from "@/templates/region/types";
import SEO from "@/components/SEO";
import { CRJMC_PRICE } from "@/constants";

interface LondonData {
  site: {
    siteMetadata: {
      siteUrl: string;
    };
  };
}

export const query = graphql`
  query LondonPage {
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`;

const londonData: RegionData = {
  name: "London",
  description: "an education and healthcare hub",
  mainMall: "White Oaks Mall and Masonville Place",
  uniqueFeature:
    "security opportunities at universities, hospitals and research facilities",
  securityOpportunities: [
    "University and college campus security",
    "Hospital and healthcare facility security",
    "Research facility protection",
    "Shopping mall security",
    "Commercial and residential complexes",
  ],
  policeServiceUrl:
    "https://www.londonpolice.ca/en/services/Record-Checks.aspx",
  majorEmployers: [
    {
      name: "Paragon Security",
      description:
        "Ontario's largest private security company (1,001-5,000 employees)",
    },
    {
      name: "Paladin Security",
      description: "Major national provider with over 10,000 employees",
    },
    {
      name: "Condor Security",
      description: "Specialized in corporate and residential security",
    },
    {
      name: "Avante Security",
      description: "High-end security services provider",
    },
  ],
  seoData: {
    title:
      "London Police Check and CRJMC | Criminal Record & Judicial Matters Check Online",
    description:
      "Get your London police check & CRJMC online in 15 minutes. Faster than London Police Service. Accepted for security guard license, employment & volunteering.",
    ogTitle:
      "London Police Check and Criminal Record & Judicial Matters Check Online | CRJMC",
    ogDescription:
      "Need a London police check or criminal record check? Get results in 15 minutes with our online CRJMC service. Accepted by employers & licensing bodies.",
  },
  schemaData: {
    name: "London Police Check & CRJMC Service",
    description:
      "Fast online London police check and criminal record check service. Alternative to London Police Service with 15-minute results. Valid for security guard license, employment screening, and volunteer positions.",
    areaServed: {
      name: "London",
      containedIn: {
        name: "Ontario",
        addressCountry: "CA",
      },
    },
  },
};

const LondonPage: React.FC<PageProps<LondonData>> = (props) => {
  return <RegionTemplate {...props} regionData={londonData} />;
};

export default LondonPage;

export const Head = () => (
  <>
    <SEO
      title={londonData.seoData.title}
      description={londonData.seoData.description}
      ogType="website"
      ogTitle={londonData.seoData.ogTitle}
      ogDescription={londonData.seoData.ogDescription}
      ogImage="/images/security-guard/security-guard-with-background-check-cleared-small.png"
    />
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: londonData.schemaData.name,
          description: londonData.schemaData.description,
          provider: {
            "@type": "Organization",
            name: "CRJMC",
            "@id": "https://crjmc.net",
            url: "https://crjmc.net",
            logo: {
              "@type": "ImageObject",
              url: "https://crjmc.net/images/crjmc-net-512.png",
            },
          },
          mainEntity: {
            "@type": "Product",
            name: `${londonData.name} Police Check & CRJMC Service`,
            description: `Online ${londonData.name} police check and criminal record check service. Faster alternative to ${londonData.name} Police Service with results in 15 minutes. Accepted for security guard licensing, employment screening, and volunteer positions.`,
            serviceType: "Background Check",
            image: {
              "@type": "ImageObject",
              url: "https://crjmc.net/images/security-guard/security-guard-with-background-check-cleared-big.jpg",
            },
            areaServed: londonData.schemaData.areaServed,
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: 4.9,
              reviewCount: 257,
              bestRating: 5,
              worstRating: 3,
            },
            offers: [
              {
                "@type": "Offer",
                name: `${londonData.name} Police Check - Standard`,
                description: `Online criminal record check for ${londonData.name} residents - results in 15 minutes`,
                price: `${CRJMC_PRICE}`,
                priceCurrency: "CAD",
                availability: "https://schema.org/InStock",
                priceValidUntil: new Date(
                  new Date().setFullYear(new Date().getFullYear() + 1),
                )
                  .toISOString()
                  .split("T")[0],
                url: `https://crjmc.net/ontario/${londonData.name.toLowerCase()}`,
              },
              {
                "@type": "Offer",
                name: `${londonData.name} CRJMC Check`,
                description: `Comprehensive criminal record and judicial matters check for ${londonData.name}`,
                price: `${CRJMC_PRICE}`,
                priceCurrency: "CAD",
                availability: "https://schema.org/InStock",
                priceValidUntil: new Date(
                  new Date().setFullYear(new Date().getFullYear() + 1),
                )
                  .toISOString()
                  .split("T")[0],
                url: `https://crjmc.net/ontario/${londonData.name.toLowerCase()}`,
              },
            ],
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: `${londonData.name} Police Check & CRJMC Services`,
              itemListElement: [
                {
                  "@type": "OfferCatalog",
                  name: "Criminal Record Checks",
                  description: `Standard criminal record checks for ${londonData.name} residents and businesses`,
                },
                {
                  "@type": "OfferCatalog",
                  name: "CRJMC Background Checks",
                  description: `Comprehensive criminal record and judicial matters checks for ${londonData.name}`,
                },
              ],
            },
          },
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://crjmc.net",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Ontario",
                item: "https://crjmc.net/ontario",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: `${londonData.name} Police Check`,
                item: `https://crjmc.net/ontario/${londonData.name.toLowerCase()}`,
              },
            ],
          },
          keywords: `${londonData.name} police check, ${londonData.name} CRJMC, ${londonData.name} criminal record check, police background check ${londonData.name}, criminal record search ${londonData.name}`,
        }),
      }}
    />
  </>
);
